'use client';

import { GridLayoutItem } from '@components/Layouts';
import { Box, Button, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import openFilterOverlayTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/openFilterOverlayTrigger';
import showFilterResultsTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilterResults/showFilterResultsTrigger';
import { CheckmarkCircled, Sliders } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { useTranslation } from '@i18n/useTranslation';
import { filterCategories, SearchResults } from '@server/careers-backend/search';
import { useSearchParams } from 'next/navigation';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { ResultCounter } from '../ResultCounter';
import { FilterFacetsContent } from './FilterFacetsContent';
import { FilterFacetsModal } from './FilterFacetsModal';
import { InternalUserFacet } from './InternalUserFacet';
import { SearchInput } from './SearchInput';
import { SelectedFilters } from './SelectedFilters';

type Props = {
  searchResults: SearchResults;
  hiddenFilters?: string[];
};

export const FilterFacets = ({ searchResults, hiddenFilters }: Props) => {
  const deferredValue = useDeferredValue(searchResults);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMainFilterButtonVisible, setIsMainFilterButtonVisible] = useState(true);
  const mainFilterButtonRef = useRef<HTMLButtonElement>(null);
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();
  const { t } = useTranslation('filterPage.filterFacets');

  const { isInternalUser } = useRequestContext();

  const hasSelectedFilters = Array.from(searchParams.keys()).some(
    key => key !== 'visibility' && key in filterCategories
  );

  useEffect(() => {
    if (searchParams.size) {
      trackEvent(
        showFilterResultsTrigger({
          option_names: searchResults.foundPages.map(foundVacancy => foundVacancy.id).join(',')
        })
      );
    }
  }, [searchResults, trackEvent, searchParams]);

  useEffect(() => {
    const button = mainFilterButtonRef.current;
    if (!button) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsMainFilterButtonVisible(entry.isIntersecting),
      { threshold: 0 }
    );

    observer.observe(button);
    return () => observer.disconnect();
  }, []);

  const visibleFilters = useMemo(() => {
    const hiddenFilterKeys = new Set(['visibility', ...(hiddenFilters ?? [])]);

    return Object.fromEntries(
      Object.entries(deferredValue.filters ?? {}).filter(([key]) => !hiddenFilterKeys.has(key))
    ) as typeof deferredValue.filters;
  }, [deferredValue, hiddenFilters]);

  return (
    <>
      {/* Desktop */}
      <GridLayoutItem
        hideUntil='md'
        column='1'
        paddingTop={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: freddyTheme.spacing[400]
        }}
      >
        <Typography variant='h2' mb={0}>
          {t('title')}
        </Typography>
        <SearchInput />
        {isInternalUser && <InternalUserFacet totalVacancies={deferredValue.pagination.total} />}
        {hasSelectedFilters && <SelectedFilters filters={visibleFilters} />}
        <FilterFacetsContent version='full' filters={visibleFilters} />
      </GridLayoutItem>

      {/* Mobile */}
      <GridLayoutItem
        hideFrom='md'
        row='1'
        column='1 / -1'
        paddingTop={false}
        padding={{ x: 400 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: freddyTheme.spacing[400]
        }}
      >
        <Flex gap={freddyTheme.spacing[200]}>
          <Button
            ref={mainFilterButtonRef}
            startIcon={<Sliders />}
            size='small'
            endIcon={
              hasSelectedFilters && (
                <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositive} />
              )
            }
            variant='secondary'
            onClick={() => {
              setIsModalOpen(true);
              trackEvent(openFilterOverlayTrigger({ sub_feature: 'top' }));
            }}
          >
            {t('title')}
          </Button>
          <SearchInput sx={{ flex: 1 }} />
        </Flex>
        <ResultCounter count={deferredValue.pagination.total} />
      </GridLayoutItem>

      {!isMainFilterButtonVisible && (
        <Box
          hideFrom='md'
          sx={{
            position: 'fixed',
            bottom: '1.2rem',
            left: 0,
            zIndex: 1,
            width: '100vw',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button
            hideFrom='md'
            startIcon={<Sliders />}
            endIcon={
              hasSelectedFilters && (
                <CheckmarkCircled
                  color={freddyTheme.color.icon.sentimentPositive}
                  innerFill='contrast'
                />
              )
            }
            onClick={() => {
              setIsModalOpen(true);
              trackEvent(openFilterOverlayTrigger({ sub_feature: 'bottom' }));
            }}
            sx={{
              width: '20rem'
            }}
          >
            {t('title')}
          </Button>
        </Box>
      )}

      <FilterFacetsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        filters={visibleFilters}
        resultCount={searchResults.pagination.total}
      />
    </>
  );
};
